/**
 * AGP — Product page (2026 redesign handoff, Page 2): editorial catalogue with a
 * category rail and a hairline 4-up grid, replacing AGP v1's Shopee-style
 * marketplace sections.
 *
 * The handoff hardcoded 21 products and prices. Those are MOCKS — this grid reads
 * the LIVE catalog (`useCatalog`) and the LIVE category list (`useCategories`), so
 * prices, stock, and the lineup stay admin-editable and correct. A card links to
 * the shared product detail page, which is where add-to-cart and variations live;
 * no commerce logic lands in this bespoke folder.
 *
 * One deliberate addition to the design: a search box. The redesign's chrome drops
 * the global search bar, so the catalogue carries its own or search is unreachable.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AGP_SITE } from '@mlm/shared';
import { useCatalog, useCategories, useProductHref } from '../../../storefront/lib/hooks';
import { useSiteContent } from '../../../storefront/lib/site-content';
import { AgpShell } from './shell';
import { fmtPrice } from './tokens';

const ALL = '__all__';

export function AgpProducts(): JSX.Element {
  const hrefFor = useProductHref();
  const sc = useSiteContent(AGP_SITE);
  const [cat, setCat] = useState<string>(ALL);
  const [search, setSearch] = useState('');

  const { products, isLoading } = useCatalog(search.trim() || undefined);
  const categories = useCategories().data ?? [];

  const shown = useMemo(
    () => (cat === ALL ? products : products.filter((p) => p.categoryId === cat)),
    [products, cat],
  );

  const activeName = categories.find((c) => c.id === cat)?.name ?? 'All Products';

  return (
    <AgpShell active="products">
      {/* Hero band */}
      <div className="agp-product-hero">
        <div
          className="agp-product-hero-overlay"
          style={{ backgroundImage: `url('${sc.img('products.hero.image')}')` }}
        />
        <div className="agp-product-hero-content">
          <div className="agp-label">{sc.text('products.hero.label')}</div>
          <h1>{sc.text('products.hero.title')}</h1>
          <p>{sc.text('products.hero.sub')}</p>
        </div>
      </div>

      <div className="agp-catalog">
        {/* Category rail — live categories, not the handoff's hardcoded five. */}
        <aside className="agp-sidebar">
          <div className="agp-sidebar-heading">Category</div>
          <ul className="agp-filter">
            <li>
              <button
                type="button"
                className={cat === ALL ? 'active' : undefined}
                onClick={() => setCat(ALL)}
              >
                All Products
              </button>
            </li>
            {categories.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  className={cat === c.id ? 'active' : undefined}
                  onClick={() => setCat(c.id)}
                >
                  {c.name}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <div className="agp-catalog-main">
          <div className="agp-catalog-header">
            <div className="agp-catalog-title">{activeName}</div>
            <input
              className="agp-search"
              type="search"
              placeholder="Search products…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search products"
            />
            <div className="agp-catalog-count">
              {isLoading ? 'Loading…' : `${shown.length} item${shown.length === 1 ? '' : 's'}`}
            </div>
          </div>

          <div className="agp-product-grid">
            {shown.map((p) => {
              const category = categories.find((c) => c.id === p.categoryId)?.name;
              return (
                <Link key={p.id} to={hrefFor(p)} className="agp-product-card">
                  <div className="agp-product-media">
                    {p.image ? (
                      <img src={p.image} alt={p.name} loading="lazy" />
                    ) : (
                      <span className="agp-product-placeholder">AGP</span>
                    )}
                  </div>
                  <div className="agp-product-body">
                    {category && <div className="agp-badge">{category}</div>}
                    <div className="agp-product-name">{p.name}</div>
                    <div className="agp-product-price">{fmtPrice(p.price)}</div>
                  </div>
                </Link>
              );
            })}

            {!isLoading && shown.length === 0 && (
              <div className="agp-empty">
                {search.trim()
                  ? `No products match “${search.trim()}”.`
                  : 'No products in this category yet.'}
              </div>
            )}
          </div>
        </div>
      </div>
    </AgpShell>
  );
}
